import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { 
  FaCog, 
  FaBell, 
  FaCreditCard, 
  FaShieldAlt
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import '../../../../assets/css/customer/dashboard/CustomerSettings.css';
import CustomerLayout from '../../../../layouts/customer/CustomerLayout';

const CustomerSettings = () => {
  // Notification Settings State
  const [notifications, setNotifications] = useState({
    bookingUpdates: true,
    paymentNotifications: true,
    eventReminders: true,
    invitationResponses: false,
    emailNotifications: true
  });

  // Payment Preferences State
  const [paymentPreferences, setPaymentPreferences] = useState({
    defaultMethod: 'card',
    savePaymentMethod: true,
    autoPayment: false
  });

  // Privacy Settings State
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    allowVendorContact: true
  });

  // Loading States
  const [loading, setLoading] = useState({
    notifications: false,
    payment: false,
    privacy: false
  });

  // Handle Notification Settings Update
  const handleNotificationUpdate = async () => {
    try {
      setLoading({ ...loading, notifications: true });
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoading({ ...loading, notifications: false });

      Swal.fire({
        icon: 'success',
        title: 'Settings Saved!',
        text: 'Notification preferences updated',
        confirmButtonColor: '#7fd3c1',
        timer: 1500,
        showConfirmButton: false
      });

    } catch (error) {
      setLoading({ ...loading, notifications: false });
      console.error('Notification update error:', error);
    }
  };

  // Handle Payment Preferences Update
  const handlePaymentPreferencesUpdate = async () => {
    try {
      setLoading({ ...loading, payment: true });
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoading({ ...loading, payment: false });

      Swal.fire({
        icon: 'success',
        title: 'Settings Saved!',
        text: 'Payment preferences updated',
        confirmButtonColor: '#7fd3c1',
        timer: 1500,
        showConfirmButton: false
      });

    } catch (error) {
      setLoading({ ...loading, payment: false });
      console.error('Payment preferences error:', error);
    }
  };

  // Handle Privacy Settings Update
  const handlePrivacyUpdate = async () => {
    try {
      setLoading({ ...loading, privacy: true });
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLoading({ ...loading, privacy: false });

      Swal.fire({
        icon: 'success',
        title: 'Settings Saved!',
        text: 'Privacy settings updated',
        confirmButtonColor: '#7fd3c1',
        timer: 1500,
        showConfirmButton: false
      });

    } catch (error) {
      setLoading({ ...loading, privacy: false });
      console.error('Privacy update error:', error);
    }
  };

  return (
    <CustomerLayout>
      <div className="cs-settings-page">
        <Container fluid>
          {/* Page Header */}
          <div className="cs-settings-header">
            <h2 className="cs-settings-title">
              <FaCog className="cs-title-icon" />
              Settings
            </h2>
            <p className="cs-settings-subtitle">Manage your profile, security & preferences</p>
          </div>

          <Row>
            {/* Left Column - Notification Settings & Payment Preferences */}
            <Col lg={6} className="mb-4">
              {/* Notification Settings */}
              <Card className="cs-settings-card">
                <Card.Header className="cs-card-header">
                  <h5 className="cs-card-title">
                    <FaBell className="cs-header-icon" />
                    Notification Settings
                  </h5>
                </Card.Header>
                <Card.Body className="cs-card-body">
                  <Form>
                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Booking Updates</label>
                        <p className="cs-notification-desc">Get notified about your booking status</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="bookingUpdates"
                        checked={notifications.bookingUpdates}
                        onChange={(e) => {
                          setNotifications({ ...notifications, bookingUpdates: e.target.checked });
                          handleNotificationUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>

                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Payment Notifications</label>
                        <p className="cs-notification-desc">Receive payment confirmations</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="paymentNotifications"
                        checked={notifications.paymentNotifications}
                        onChange={(e) => {
                          setNotifications({ ...notifications, paymentNotifications: e.target.checked });
                          handleNotificationUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>

                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Event Reminders</label>
                        <p className="cs-notification-desc">Get reminded before your events</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="eventReminders"
                        checked={notifications.eventReminders}
                        onChange={(e) => {
                          setNotifications({ ...notifications, eventReminders: e.target.checked });
                          handleNotificationUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>

                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Invitation Responses</label>
                        <p className="cs-notification-desc">Track guest RSVP responses</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="invitationResponses"
                        checked={notifications.invitationResponses}
                        onChange={(e) => {
                          setNotifications({ ...notifications, invitationResponses: e.target.checked });
                          handleNotificationUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>

                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Email Notifications</label>
                        <p className="cs-notification-desc">Receive updates via email</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="emailNotifications"
                        checked={notifications.emailNotifications}
                        onChange={(e) => {
                          setNotifications({ ...notifications, emailNotifications: e.target.checked });
                          handleNotificationUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              {/* Payment Preferences */}
              <Card className="cs-settings-card">
                <Card.Header className="cs-card-header">
                  <h5 className="cs-card-title">
                    <FaCreditCard className="cs-header-icon" />
                    Payment Preferences
                  </h5>
                </Card.Header>
                <Card.Body className="cs-card-body">
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label className="cs-form-label">Default Payment Method</Form.Label>
                      <Form.Select
                        value={paymentPreferences.defaultMethod}
                        onChange={(e) => {
                          setPaymentPreferences({ ...paymentPreferences, defaultMethod: e.target.value });
                          handlePaymentPreferencesUpdate();
                        }}
                        className="cs-form-select"
                      >
                        <option value="card">Credit/Debit Card</option>
                        <option value="jazzcash">JazzCash</option>
                        <option value="easypaisa">EasyPaisa</option>
                        <option value="bank">Bank Transfer</option>
                      </Form.Select>
                    </Form.Group>

                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Save Payment Method</label>
                        <p className="cs-notification-desc">Save for future use</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="savePaymentMethod"
                        checked={paymentPreferences.savePaymentMethod}
                        onChange={(e) => {
                          setPaymentPreferences({ ...paymentPreferences, savePaymentMethod: e.target.checked });
                          handlePaymentPreferencesUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>

                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Auto Payment</label>
                        <p className="cs-notification-desc">Enable for recurring services</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="autoPayment"
                        checked={paymentPreferences.autoPayment}
                        onChange={(e) => {
                          setPaymentPreferences({ ...paymentPreferences, autoPayment: e.target.checked });
                          handlePaymentPreferencesUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column - Privacy Settings Only */}
            <Col lg={6} className="mb-4">
              {/* Privacy Settings */}
              <Card className="cs-settings-card">
                <Card.Header className="cs-card-header">
                  <h5 className="cs-card-title">
                    <FaShieldAlt className="cs-header-icon" />
                    Privacy Settings
                  </h5>
                </Card.Header>
                <Card.Body className="cs-card-body">
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label className="cs-form-label">Profile Visibility</Form.Label>
                      <Form.Select
                        value={privacySettings.profileVisibility}
                        onChange={(e) => {
                          setPrivacySettings({ ...privacySettings, profileVisibility: e.target.value });
                          handlePrivacyUpdate();
                        }}
                        className="cs-form-select"
                      >
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                        <option value="friends">Friends Only</option>
                      </Form.Select>
                    </Form.Group>

                    <div className="cs-notification-item">
                      <div className="cs-notification-info">
                        <label className="cs-notification-label">Vendor Contact</label>
                        <p className="cs-notification-desc">Allow vendors to contact me</p>
                      </div>
                      <Form.Check
                        type="switch"
                        id="allowVendorContact"
                        checked={privacySettings.allowVendorContact}
                        onChange={(e) => {
                          setPrivacySettings({ ...privacySettings, allowVendorContact: e.target.checked });
                          handlePrivacyUpdate();
                        }}
                        className="cs-switch"
                      />
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </CustomerLayout>
  );
};

export default CustomerSettings;